import React, { useRef, useState } from "react";
import { Image } from "antd";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import useBreakpoint from "../../../../utils/useBreakpoint";
import shareTourLink from "../shareTourLink";

const ICONS = "/iceholidays-assets/images/icons";
const FALLBACK_IMAGE = "/iceholidays-assets/images/china_listings_cover.png";

// How far a finger must travel before it counts as a swipe (or as a scroll, if vertical).
const AXIS_LOCK_PX = 6;
// A swipe commits once it passes this share of the gallery's width (capped, so wide frames stay easy to flick).
const SWIPE_COMMIT_RATIO = 0.2;
const SWIPE_COMMIT_MAX_PX = 60;
// Dragging past the first/last photo moves the track by only this fraction, so it feels like an edge.
const EDGE_RESISTANCE = 0.35;
// The mobile design shows a short run of dots, not one per photo (a 22-photo tour would run under the pill).
const MAX_MOBILE_DOTS = 4;

interface Props {
  images: string[];
}

// Sliding photo carousel + "View All Photos (N)" pill + dot pagination + prev/next arrows (desktop) and
// swipe (touch), per Figma. Every slide sits in one track that translates, so a swipe drags the photos
// with the finger and settles with a slide instead of swapping the image. Reuses antd Image.PreviewGroup
// for the lightbox (the mechanism components/shared/ItineraryImages.tsx also relies on) rather than that
// component's chrome, since the visual spec here (dark pill w/ count, pill+dot pagination) differs
// materially from the existing series show-page gallery.
export default function GroundTourGallery({ images }: Props) {
  const isMobile = useBreakpoint();
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const slides = images.length > 0 ? images : [FALLBACK_IMAGE];
  const lastIndex = slides.length - 1;

  const viewportRef = useRef<HTMLDivElement>(null);
  const touch = useRef<{ x: number; y: number; axis: "x" | "y" | null } | null>(null);
  const suppressClick = useRef(false);

  const step = (delta: number) => {
    setActiveIndex((current) => (current + delta + slides.length) % slides.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const point = e.touches[0];
    if (point) touch.current = { x: point.clientX, y: point.clientY, axis: null };
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const start = touch.current;
    const point = e.touches[0];
    if (!start || !point) return;

    const dx = point.clientX - start.x;
    const dy = point.clientY - start.y;

    if (start.axis === null) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      start.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    }

    // A vertical gesture belongs to the page's scroll (the viewport allows it via touch-action: pan-y).
    if (start.axis !== "x") return;

    const pastEdge = (activeIndex === 0 && dx > 0) || (activeIndex === lastIndex && dx < 0);
    setDragging(true);
    setDragX(pastEdge ? dx * EDGE_RESISTANCE : dx);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    if (!start || start.axis !== "x") return;

    const dx = (e.changedTouches[0]?.clientX ?? start.x) - start.x;
    const width = viewportRef.current?.clientWidth || 1;
    const commit = Math.min(SWIPE_COMMIT_MAX_PX, width * SWIPE_COMMIT_RATIO);

    if (dx <= -commit && activeIndex < lastIndex) setActiveIndex(activeIndex + 1);
    else if (dx >= commit && activeIndex > 0) setActiveIndex(activeIndex - 1);

    setDragging(false);
    setDragX(0);

    // A short horizontal drag can still be followed by a click; it shouldn't open the lightbox.
    suppressClick.current = true;
    window.setTimeout(() => { suppressClick.current = false; }, 350);
  };

  const onTouchCancel = () => {
    touch.current = null;
    setDragging(false);
    setDragX(0);
  };

  const openPreview = () => {
    if (!suppressClick.current) setPreviewVisible(true);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setPreviewVisible(true);
    } else if (e.key === "ArrowLeft" && slides.length > 1) {
      step(-1);
    } else if (e.key === "ArrowRight" && slides.length > 1) {
      step(1);
    }
  };

  // Mobile shows a short window of dots that follows the active photo; desktop keeps one per photo.
  const windowed = isMobile && slides.length > MAX_MOBILE_DOTS;
  const dotStart = windowed ? Math.min(Math.max(activeIndex - 1, 0), slides.length - MAX_MOBILE_DOTS) : 0;
  const dotIndexes = slides.map((_, index) => index).slice(dotStart, windowed ? dotStart + MAX_MOBILE_DOTS : undefined);

  return (
    <div className="ground-tour-show-v2__gallery">
      {/* Blurred backdrop shows through the margins once the frame narrows on desktop. */}
      <div
        className="ground-tour-show-v2__gallery-backdrop"
        style={{ backgroundImage: `url(${slides[activeIndex]})` }}
        aria-hidden="true"
      />
      <div className="ground-tour-show-v2__gallery-scrim" aria-hidden="true" />

      <div className="ground-tour-show-v2__gallery-frame">
        <div
          ref={viewportRef}
          role="button"
          tabIndex={0}
          className="ground-tour-show-v2__gallery-image"
          onClick={openPreview}
          onKeyDown={onKeyDown}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchCancel}
          aria-label="View photo gallery"
        >
          <div
            className={`ground-tour-show-v2__gallery-track ${dragging ? "is-dragging" : ""}`}
            style={{ transform: `translate3d(calc(${-activeIndex * 100}% + ${dragX}px), 0, 0)` }}
          >
            {slides.map((src, index) => (
              <div className="ground-tour-show-v2__gallery-slide" key={`${src}-${index}`}>
                <img src={src} alt="" draggable={false} loading={index === 0 ? "eager" : "lazy"} decoding="async" />
              </div>
            ))}
          </div>
        </div>

        {slides.length > 1 && !isMobile && (
          <>
            <button
              type="button"
              className="ground-tour-show-v2__gallery-arrow ground-tour-show-v2__gallery-arrow--prev"
              onClick={() => step(-1)}
              aria-label="Previous photo"
            >
              <Icon path={mdiChevronLeft} size="20px" />
            </button>
            <button
              type="button"
              className="ground-tour-show-v2__gallery-arrow ground-tour-show-v2__gallery-arrow--next"
              onClick={() => step(1)}
              aria-label="Next photo"
            >
              <Icon path={mdiChevronRight} size="20px" />
            </button>
          </>
        )}

        {/* Mobile (Figma 10102:248616): Share sits on the image instead of in the header. */}
        {isMobile && (
          <button type="button" className="ground-tour-show-v2__gallery-share" onClick={shareTourLink} aria-label="Share">
            <img src={`${ICONS}/icon-gallery-share.svg`} alt="" aria-hidden="true" />
          </button>
        )}

        {slides.length > 1 && (
          <button
            type="button"
            className="ground-tour-show-v2__gallery-viewall"
            onClick={() => setPreviewVisible(true)}
          >
            <img src={`${ICONS}/${isMobile ? "icon-all-photos.svg" : "icon-view-all-photos.png"}`} alt="" aria-hidden="true" />
            {isMobile ? "All Photos" : "View All Photos"} ({slides.length})
          </button>
        )}

        {slides.length > 1 && (
          <div className="ground-tour-show-v2__gallery-dots">
            {dotIndexes.map((index) => (
              <button
                key={index}
                type="button"
                aria-label={`Photo ${index + 1}`}
                className={`ground-tour-show-v2__gallery-dot ${index === activeIndex ? "is-active" : ""}`}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "none" }}>
        <Image.PreviewGroup
          items={slides}
          preview={{
            visible: previewVisible,
            current: activeIndex,
            onVisibleChange: (visible) => setPreviewVisible(visible),
            onChange: (current) => setActiveIndex(current),
          }}
        />
      </div>
    </div>
  );
}
